<script setup lang="ts">

import {mergeProps, ref, watch} from "vue";
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import {Assessment} from "@/types";

interface Props {
    assessment: Assessment
    hidden?: boolean;
    hideAdminActions?: boolean;
    readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    hidden: false,
    hideAdminActions: true,
    readonly: false,
});

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'open'): void;
    (e: 'delete'): void;
    (e: 'star'): void;
}>();

const confirmName = ref<string>('');

const isDeleteAssessmentDialogOpen = ref<boolean>(false);

watch(isDeleteAssessmentDialogOpen, (value) => {
    if (value) {
        confirmName.value = '';
    }
});
</script>

<template>
    <v-menu
        v-if="!props.hidden"
        location="bottom end"
    >
        <template #activator="{ props: bind }">
            <v-tooltip
                location="top"
                content-class="bg-black border-sm text-tooltip"
                max-width="200"
            >
                <template #activator="{ props: subBind }">
                    <v-icon-btn
                        v-bind="mergeProps(bind, subBind)"
                        icon="mdi-dots-vertical"
                        size="32"
                        class="ml-2"
                        rounded="sm"
                    />
                </template>
                More actions
            </v-tooltip>
        </template>
        <v-list
            rounded="lg"
            border="sm"
            elevation="3"
            class="bg-background py-0 my-2"
            width="250"
        >
            <v-list-item
                v-if="!props.hideAdminActions"
                class="text-body-2 ma-1 pl-2"
                link
                density="compact"
                @click="emit('star')"
            >
                <template #prepend>
                    <v-icon>
                        {{ props.assessment.is_starred ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                </template>
                {{ props.assessment.is_starred ? 'Unmark' : 'Mark' }} as starred
            </v-list-item>
            <v-list-item
                v-if="!props.readonly"
                class="text-body-2 ma-1 pl-2"
                link
                density="compact"
                @click="props.assessment.status === 'closed' ? emit('open') : emit('close')"
            >
                <template #prepend>
                    <v-icon>
                        {{ props.assessment.status === 'closed' ? 'mdi-circle-outline' : 'mdi-minus-circle-outline' }}
                    </v-icon>
                </template>
                {{ props.assessment.status === 'closed' ? 'Reopen' : 'Close' }} assessment
            </v-list-item>
            <v-divider />
            <v-list-item
                class="text-body-2 ma-1 pl-2 text-error"
                link
                density="compact"
                @click="isDeleteAssessmentDialogOpen = true"
            >
                <template #prepend>
                    <v-icon>
                        mdi-delete-outline
                    </v-icon>
                </template>
                Delete assessment
            </v-list-item>
        </v-list>
    </v-menu>
    <delete-dialog
        v-model="isDeleteAssessmentDialogOpen"
        :disabled="confirmName !== props.assessment.title"
        @submit="emit('delete')"
    >
        <template #title>
            Are you absolutely sure?
        </template>
        <template #default>
            <v-alert
                variant="tonal"
                color="red-lighten-3"
                class="mb-3"
                icon="mdi-alert-circle-outline"
                icon-size="20"
                density="compact"
            >
                <span class="text-black text-body-2 font-weight-regular">
                    This action cannot be undone. All answers and internal comments associated with this assessment will be permanently deleted.
                </span>
            </v-alert>
            This action can lead to data loss. To prevent accidental actions we ask you to confirm your intention.
            <v-field-label class="mt-3">
                Enter the following to confirm:
            </v-field-label>
            <code class="bg-grey-lighten-3 pa-1 rounded">
                {{ props.assessment.title }}
            </code>
            <v-text-field
                v-model="confirmName"
                name="confirmName"
                density="compact"
                class="mt-2"
                hide-details
            />
        </template>
        <template #label>
            Confirm
        </template>
    </delete-dialog>
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 28px;
}

.v-list-item--density-compact.v-list-item--one-line {
    min-height: 32px;
}
</style>
